import {
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  PieChart,
  Target,
  Cpu,
  Wallet,
  AlertTriangle,
  Scale,
  Download,
  Linkedin,
  X,
  Facebook,
  Instagram,
  Clock,
  UserPlus
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenu,
} from "@/components/ui/sidebar";
import { trackUserAction } from "@/utils/tracking";
import { InviteDialog } from "@/components/InviteDialog";
import { supabase } from "@/integrations/supabase/client";

interface NavItem {
  title: string;
  icon: typeof FileText;
  href: string;
  external?: boolean;
}

const navItems: NavItem[] = [
  { title: "Executive Summary", icon: FileText, href: "/business-summary" },
  { title: "Company Journey", icon: Clock, href: "/history" },
  { title: "Product/Technology", icon: Cpu, href: "/product" },
  { title: "Market Opportunity", icon: Target, href: "/market" },
  { title: "Traction/Product-Market Fit", icon: TrendingUp, href: "/traction" },
  { title: "Team & Roles", icon: Users, href: "/team" },
  { title: "Financial Statements", icon: FileText, href: "/financial-statements" },
  { title: "Cap Table Summary", icon: PieChart, href: "/cap-table" },
  { title: "Investment/Use of Funds", icon: Wallet, href: "/use-of-funds" },
  { title: "Risks and Mitigation", icon: AlertTriangle, href: "/risks" },
  { title: "Legal and Compliance", icon: Scale, href: "/legal" },
  { title: "Archived Newsletter / Pitch Decks", icon: Download, href: "/archived-newsletter" },
];

const socialLinks: NavItem[] = [
  { title: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/company/supplyfied", external: true },
  { title: "X", icon: X, href: "https://x.com/supplyfied?s=21", external: true },
  { title: "Facebook", icon: Facebook, href: "https://www.facebook.com/supplyfied.sa1/", external: true },
  { title: "Instagram", icon: Instagram, href: "https://www.instagram.com/supplyfied/", external: true },
];

export function DataRoomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [canInviteUsers, setCanInviteUsers] = useState(false);

  useEffect(() => {
    const checkUserPermissions = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const email = session?.user?.email;
      const isAdmin = email === 'ahmed@supplyfied.com' || email === 'nour.diaa@supplyfied.com';
      setCanInviteUsers(isAdmin);
    };

    checkUserPermissions();
  }, []);

  const handleNavigation = async (item: NavItem) => {
    if (item.external) {
      await trackUserAction("page_visit", { page_path: item.href });
      window.open(item.href, "_blank");
      return;
    }

    await trackUserAction("page_visit", { page_path: item.href });
    navigate(item.href);
  };

  return (
    <Sidebar>
      <SidebarContent>
        <div className="flex justify-center mb-8 pt-6">
          <img
            src="/lovable-uploads/675646d7-dd70-4076-b830-c1b35b3f0ed4.png"
            alt="Supplyfied Logo"
            className="h-14 w-auto transition-transform duration-300 hover:scale-105"
          />
        </div>
        <SidebarGroup>
          <SidebarGroupLabel className="text-lg font-semibold tracking-wide text-primary px-4 mb-4">
            Investor Hub
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <button
                      onClick={() => handleNavigation(item)}
                      className={`flex items-center gap-3 w-full px-4 py-2.5 rounded-md transition-all duration-200 ease-in-out hover:bg-primary/10 ${
                        location.pathname === item.href
                          ? "bg-primary/15 text-primary font-medium shadow-sm"
                          : "text-muted-foreground hover:text-primary"
                      }`}
                    >
                      <item.icon className={`h-4.5 w-4.5 transition-transform duration-200 ${
                        location.pathname === item.href ? "scale-110" : ""
                      }`} />
                      <span className="transition-transform duration-200 hover:translate-x-0.5">
                        {item.title}
                      </span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        {/* Invite Button - Only show for admin users */}
        {canInviteUsers && (
          <SidebarGroup className="mt-auto mb-4">
            <SidebarGroupContent>
              <div className="px-4">
                <InviteDialog>
                  <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                    <UserPlus className="h-4 w-4" />
                    <span>Invite Users</span>
                  </button>
                </InviteDialog>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        
        {/* Social Media Links */}
        <SidebarGroup className="mb-6">
          <SidebarGroupContent>
            <div className="flex justify-center gap-4 px-4">
              {socialLinks.map((item) => (
                <button
                  key={item.title}
                  onClick={() => handleNavigation(item)}
                  className="text-muted-foreground hover:text-primary transition-colors duration-200"
                  title={item.title}
                >
                  <item.icon className="h-5 w-5" />
                </button>
              ))}
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}